import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Tag, TagMutationPayload } from '../../_service/data.service';
import { DataService } from '../../_service/data.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ConfirmationService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

type TagEditForm = {
  _id: string;
  name: string;
  description: string;
  tagType: string;
  slug: string;
  color: string;
  icon: string;
  isActive: boolean;
  sortOrder: number | null;
};

type TagSortColumn = 'name' | 'color' | 'type' | 'usedIn';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-edit-tags',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ColorPickerModule,
    ConfirmDialogModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    TooltipModule,
    TagModule,
  ],
  providers: [ConfirmationService],
  styles: [
    `
      .tag-color-swatch {
        display: inline-block;
        width: 14px;
        height: 14px;
        border-radius: 3px;
        border: 1px solid rgba(0,0,0,0.15);
        vertical-align: middle;
        flex-shrink: 0;
      }

      .tag-name-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        border: 1px solid #cbd5e1;
        border-radius: 9999px;
        padding: 0.12rem 0.6rem;
        max-width: 100%;
      }

      .tag-name-chip__text {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .tag-color-palette {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
      }

      .tag-color-palette__btn {
        width: 1.35rem;
        height: 1.35rem;
        border-radius: 9999px;
        border: 2px solid transparent;
        box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.15);
        transition: transform 0.12s ease, box-shadow 0.12s ease;
      }

      .tag-color-palette__btn:hover {
        transform: translateY(-1px);
      }

      .tag-color-palette__btn--selected {
        border-color: #0f172a;
        box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.95);
      }

      th {
        background: var(--p-primary-color);
        color: var(--p-primary-contrast-color, #fff);
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        padding: 0.5rem 0.75rem;
        white-space: nowrap;
      }

      td {
        padding: 0.35rem 0.75rem;
        vertical-align: middle;
        border-top: 1px solid #e2e8f0;
      }

      tr:hover td {
        background-color: #f1f5f9;
      }
    `,
  ],
  template: `
    <div class="w-full rounded-2xl border border-slate-200 bg-white/80 p-6 pb-4 shadow-sm backdrop-blur">

      <!-- Header -->
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 class="text-xl font-semibold text-slate-900">Edit Tags</h2>
          <p class="text-sm text-slate-500">Review, rename, and colorize your tags.</p>
        </div>
        <div class="flex items-center rounded-2xl bg-slate-900/90 px-4 py-2 text-white shadow-inner">
          <span class="text-2xl font-semibold leading-none">{{ tags.length }}</span>
          <span class="ml-2 text-xs uppercase tracking-widest text-slate-300">total tags</span>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="mt-4 flex flex-wrap items-center gap-2">
        <button
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:text-slate-900"
          routerLink="/home">
          <span class="material-icons text-base" aria-hidden="true">home</span>
          Home
        </button>
        <button
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:text-slate-900"
          routerLink="/admin">
          <span class="material-icons text-base" aria-hidden="true">arrow_back</span>
          Back to Admin
        </button>
        <button
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:text-slate-900"
          (click)="loadData()">
          <span class="material-icons text-base" aria-hidden="true">refresh</span>
          Refresh
        </button>
        <button
          class="group relative flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-emerald-500/30 transition hover:-translate-y-0.5"
          (click)="openAddDialog()"
          aria-label="Add new tag">
          <span class="material-icons text-base" aria-hidden="true">add_box</span>
          Add Tag
        </button>
      </div>

      <!-- Loading / empty states -->
      <div class="mt-6">
        <ng-container *ngIf="tempMessage; else dataLoaded">
          <div class="flex items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-6 text-slate-500">
            <span class="material-icons animate-spin text-slate-400" aria-hidden="true">autorenew</span>
            <span>{{ tempMessage }}</span>
          </div>
        </ng-container>

        <ng-template #dataLoaded>
          <ng-container *ngIf="tags.length; else emptyState">

            <!-- Search bar -->
            <div class="mb-3 flex items-center gap-2">
              <span class="material-icons text-slate-400" aria-hidden="true">search</span>
              <input
                pInputText
                class="w-full max-w-xs rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                placeholder="Filter tags…"
                [(ngModel)]="searchQuery"
                autocomplete="off" />
              <button
                *ngIf="searchQuery"
                type="button"
                class="ml-1 text-slate-400 hover:text-slate-700"
                (click)="searchQuery = ''"
                aria-label="Clear search">
                <span class="material-icons text-base">close</span>
              </button>
              <span class="ml-auto text-xs text-slate-400">{{ filteredTags.length }} of {{ tags.length }} shown</span>
            </div>

            <!-- Table -->
            <div class="rounded-2xl border border-slate-200 shadow-sm overflow-auto max-h-[72vh]">
              <table class="w-full border-collapse text-sm text-slate-700">
                <thead class="sticky top-0 z-10">
                  <tr>
                    <th class="text-left">
                      <button type="button" class="inline-flex items-center gap-1" (click)="toggleSort('color')">
                        Color
                        <span class="material-icons text-[0.8rem] leading-none">{{ getSortIcon('color') }}</span>
                      </button>
                    </th>
                    <th class="text-left">
                      <button type="button" class="inline-flex items-center gap-1" (click)="toggleSort('name')">
                        Name
                        <span class="material-icons text-[0.8rem] leading-none">{{ getSortIcon('name') }}</span>
                      </button>
                    </th>
                    <th class="text-left">Slug</th>
                    <th class="text-left">Description</th>
                    <th class="text-left">
                      <button type="button" class="inline-flex items-center gap-1" (click)="toggleSort('type')">
                        Type
                        <span class="material-icons text-[0.8rem] leading-none">{{ getSortIcon('type') }}</span>
                      </button>
                    </th>
                    <th class="text-center">
                      <button type="button" class="inline-flex items-center gap-1" (click)="toggleSort('usedIn')">
                        Used&nbsp;in
                        <span class="material-icons text-[0.8rem] leading-none">{{ getSortIcon('usedIn') }}</span>
                      </button>
                    </th>
                    <th class="text-center">Active</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let tag of filteredTags; trackBy: trackByTagId">
                    <!-- Color swatch -->
                    <td class="w-10">
                      <span
                        class="tag-color-swatch"
                        [style.background-color]="tag.color || 'transparent'"
                        [title]="tag.color || 'No color'"
                        aria-hidden="true">
                      </span>
                    </td>

                    <!-- Name -->
                    <td class="font-medium text-slate-900 min-w-[140px]">
                      <button
                        type="button"
                        class="tag-name-chip cursor-pointer"
                        (click)="editTag(tag)"
                        pTooltip="Edit tag"
                        tooltipPosition="top"
                        appendTo="body"
                        [style.background-color]="getTagChipBackground(tag.color)"
                        [style.border-color]="getNormalizedColor(tag.color) || '#cbd5e1'"
                        [style.color]="getReadableTextColor(getTagChipBackground(tag.color))">
                        <span class="tag-color-swatch" [style.background-color]="getNormalizedColor(tag.color) || 'transparent'"></span>
                        <span class="tag-name-chip__text">{{ tag.name }}</span>
                      </button>
                    </td>

                    <!-- Slug -->
                    <td class="font-mono text-xs text-slate-500">{{ tag.slug }}</td>

                    <!-- Description -->
                    <td class="text-slate-600 min-w-[220px] max-w-[420px]">
                      <span [title]="tag.description || ''">{{ tag.description || '—' }}</span>
                    </td>

                    <!-- Type -->
                    <td class="text-slate-500">{{ tag.tagType || '—' }}</td>

                    <!-- Usage count -->
                    <td class="text-center">
                      <span
                        class="inline-block rounded-full px-2 py-0.5 text-xs font-semibold"
                        [ngClass]="getUsageBadgeClass(tag._id)">
                        {{ getUsageCount(tag._id) }}
                      </span>
                    </td>

                    <!-- Active -->
                    <td class="text-center">
                      <span
                        class="material-icons text-base"
                        [ngClass]="tag.isActive ? 'text-emerald-500' : 'text-slate-300'"
                        [title]="tag.isActive ? 'Active' : 'Inactive'">
                        {{ tag.isActive ? 'check_circle' : 'radio_button_unchecked' }}
                      </span>
                    </td>

                    <!-- Actions -->
                    <td class="text-center">
                      <div class="flex justify-center gap-1">
                        <button
                          class="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:-translate-y-0.5 hover:border-blue-200 hover:text-blue-600"
                          (click)="editTag(tag)"
                          pTooltip="Edit tag"
                          tooltipPosition="top"
                          appendTo="body"
                          aria-label="Edit tag">
                          <span class="material-icons text-sm" aria-hidden="true">edit</span>
                        </button>
                        <button
                          class="inline-flex h-7 w-7 items-center justify-center rounded-lg border transition hover:-translate-y-0.5"
                          [ngClass]="canDelete(tag._id)
                            ? 'border-rose-200 bg-rose-50 text-rose-600 hover:border-rose-300 hover:bg-rose-100 hover:text-rose-700'
                            : 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'"
                          (click)="canDelete(tag._id) && deleteTag(tag)"
                          [pTooltip]="canDelete(tag._id) ? 'Delete tag' : 'Cannot delete — tag is in use'"
                          tooltipPosition="top"
                          appendTo="body"
                          [attr.aria-label]="canDelete(tag._id) ? 'Delete tag' : 'Tag is in use, cannot delete'">
                          <span class="material-icons text-sm" aria-hidden="true">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </ng-container>
        </ng-template>

        <ng-template #emptyState>
          <div class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 via-white to-slate-50 px-6 py-10 text-center text-slate-500">
            <span class="material-icons text-4xl text-slate-400" aria-hidden="true">label</span>
            <div>
              <p class="text-base font-semibold text-slate-700">No tags found</p>
              <p class="text-sm">Add your first tag to start organizing content.</p>
            </div>
            <button
              class="rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5"
              (click)="openAddDialog()">
              Add tag
            </button>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Edit / Add Dialog -->
    <p-dialog
      [(visible)]="editDialogVisible"
      (onHide)="resetEditDialog()"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(600px, 95vw)' }"
      [header]="editingTag ? 'Edit Tag' : 'New Tag'"
      appendTo="body">
      <div class="flex flex-col gap-5">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">

          <!-- ID (readonly, only shown when editing) -->
          <div *ngIf="editingTag" class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="tagId">ID</label>
            <input
              id="tagId"
              pInputText
              class="w-full text-xs font-mono"
              [ngModel]="editForm._id"
              readonly
              aria-readonly="true" />
          </div>

          <!-- Name -->
          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="tagName">Name *</label>
            <input
              id="tagName"
              pInputText
              class="w-full"
              [(ngModel)]="editForm.name"
              autocomplete="off"
              placeholder="Tag name" />
          </div>

          <!-- Description -->
          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="tagDescription">Description</label>
            <textarea
              id="tagDescription"
              pInputTextarea
              class="w-full"
              [(ngModel)]="editForm.description"
              [autoResize]="true"
              rows="3"
              autocomplete="off"
              placeholder="Short description of this tag"></textarea>
          </div>

          <!-- Slug -->
          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="tagSlug">Slug</label>
            <input
              id="tagSlug"
              pInputText
              class="w-full font-mono text-sm"
              [(ngModel)]="editForm.slug"
              autocomplete="off"
              placeholder="auto-generated if empty" />
          </div>

          <!-- Tag Type -->
          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="tagType">Tag Type</label>
            <input
              id="tagType"
              pInputText
              class="w-full"
              [(ngModel)]="editForm.tagType"
              autocomplete="off"
              placeholder="e.g. topic, project…" />
          </div>

          <!-- Color -->
          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="tagColor">Color</label>
            <div class="flex items-center gap-3">
              <p-colorPicker
                inputId="tagColor"
                format="hex"
                [(ngModel)]="editForm.color"
                (onChange)="setColorFromInput($event.value)">
              </p-colorPicker>
              <input
                pInputText
                class="w-full font-mono text-sm"
                [ngModel]="getColorHex()"
                (ngModelChange)="setColorFromInput($event)"
                placeholder="#RRGGBB"
                autocomplete="off" />
              <button
                *ngIf="editForm.color"
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-rose-400 transition hover:border-rose-200 hover:text-rose-600"
                (click)="editForm.color = ''"
                pTooltip="Clear color"
                tooltipPosition="top"
                appendTo="body"
                aria-label="Clear color">
                <span class="material-icons text-sm" aria-hidden="true">format_color_reset</span>
              </button>
              <!-- Preview swatch -->
              <span
                class="tag-color-swatch h-8 w-8 rounded-md"
                [style.background-color]="editForm.color || 'transparent'"
                aria-hidden="true">
              </span>
            </div>
            <div class="tag-color-palette">
              <button
                *ngFor="let preset of commonTagColors"
                type="button"
                class="tag-color-palette__btn"
                [ngClass]="{ 'tag-color-palette__btn--selected': isPresetSelected(preset.value) }"
                [style.background-color]="preset.value"
                [title]="preset.name + ' (' + preset.value + ')'"
                [attr.aria-label]="'Pick ' + preset.name + ' color'"
                (click)="pickCommonColor(preset.value)">
              </button>
            </div>
          </div>

          <!-- Active toggle -->
          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition"
                [ngClass]="editForm.isActive
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'"
                (click)="editForm.isActive = !editForm.isActive">
                <span
                  class="material-icons text-base"
                  [ngClass]="editForm.isActive ? 'text-emerald-500' : 'text-slate-300'">
                  {{ editForm.isActive ? 'toggle_on' : 'toggle_off' }}
                </span>
                {{ editForm.isActive ? 'Active' : 'Inactive' }}
              </button>
            </div>
          </div>

        </div>

        <!-- Dialog actions -->
        <div class="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            class="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
            (click)="resetEditDialog()">
            Cancel
          </button>
          <p-button
            [label]="editingTag ? 'Save changes' : 'Create tag'"
            icon="pi pi-check"
            styleClass="p-button-rounded p-button-sm"
            [disabled]="!editForm.name.trim()"
            (onClick)="saveTagEdits()">
          </p-button>
        </div>
      </div>
    </p-dialog>

    <p-confirmDialog appendTo="body"></p-confirmDialog>
  `,
})
export class EditTagsComponent implements OnInit {
  commonTagColors: Array<{ name: string; value: string }> = [
    { name: 'Slate', value: '#64748B' },
    { name: 'Gray', value: '#6B7280' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Amber', value: '#F59E0B' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Lime', value: '#84CC16' },
    { name: 'Green', value: '#22C55E' },
    { name: 'Emerald', value: '#10B981' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Sky', value: '#0EA5E9' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Violet', value: '#8B5CF6' },
    { name: 'Purple', value: '#A855F7' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Rose', value: '#F43F5E' },
  ];

  tags: Tag[] = [];
  /** tag._id → count of notes referencing the tag */
  private usageCounts = new Map<string, number>();
  tempMessage = 'Loading tags…';
  searchQuery = '';
  sortColumn: TagSortColumn = 'name';
  sortDirection: SortDirection = 'asc';
  editDialogVisible = false;
  editForm: TagEditForm = this.emptyForm();
  editingTag: Tag | null = null;
  isSaving = false;

  constructor(
    private dataService: DataService,
    private snackbarService: SnackbarService,
    private confirmationService: ConfirmationService,
  ) {}

  // ────────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadData();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Data loading
  // ────────────────────────────────────────────────────────────────────────────

  loadData(): void {
    this.tempMessage = 'Loading…';

    forkJoin({
      tags: this.dataService.getAllTags(),
      notes: this.dataService.getAllNotes(),
    })
      .pipe(finalize(() => (this.tempMessage = '')))
      .subscribe({
        next: ({ tags, notes }) => {
          this.tags = Array.isArray(tags?.data)
            ? [...tags.data].sort((a, b) => a.name.localeCompare(b.name))
            : [];

          // Build usage map: for each note count all tag ids it references
          const counts = new Map<string, number>();
          const noteList = Array.isArray(notes?.data) ? notes.data : [];
          for (const note of noteList) {
            const tagRefs = Array.isArray(note.Tags) ? note.Tags : [];
            for (const ref of tagRefs) {
              const id = typeof ref === 'string' ? ref : (ref as Tag)?._id;
              if (id) {
                counts.set(id, (counts.get(id) ?? 0) + 1);
              }
            }
          }
          this.usageCounts = counts;
        },
        error: (err) => {
          console.error('EditTags: loadData error', err);
          this.tempMessage = 'Error loading data.';
          this.snackbarService.show('Failed to load tags.', 'error');
        },
      });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Derived helpers
  // ────────────────────────────────────────────────────────────────────────────

  get filteredTags(): Tag[] {
    const q = this.searchQuery.trim().toLowerCase();
    const filtered = this.tags.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        (t.tagType ?? '').toLowerCase().includes(q),
    );

    const directionMultiplier = this.sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let result = 0;

      if (this.sortColumn === 'name') {
        result = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      } else if (this.sortColumn === 'color') {
        const aColor = this.normalizeHexValue(a.color ?? '');
        const bColor = this.normalizeHexValue(b.color ?? '');
        result = aColor.localeCompare(bColor, undefined, { sensitivity: 'base' });
        if (result === 0) {
          result = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }
      } else if (this.sortColumn === 'type') {
        result = (a.tagType ?? '').localeCompare((b.tagType ?? ''), undefined, { sensitivity: 'base' });
        if (result === 0) {
          result = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }
      } else if (this.sortColumn === 'usedIn') {
        result = this.getUsageCount(a._id) - this.getUsageCount(b._id);
        if (result === 0) {
          result = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }
      }

      return result * directionMultiplier;
    });
  }

  toggleSort(column: TagSortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortColumn = column;
    this.sortDirection = 'asc';
  }

  getSortIcon(column: TagSortColumn): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }

    return this.sortDirection === 'asc' ? 'north' : 'south';
  }

  getUsageCount(tagId: string): number {
    return this.usageCounts.get(tagId) ?? 0;
  }

  canDelete(tagId: string): boolean {
    return this.getUsageCount(tagId) === 0;
  }

  getUsageBadgeClass(tagId: string): string {
    const count = this.getUsageCount(tagId);
    if (count === 0) return 'bg-slate-100 text-slate-400';
    if (count <= 5) return 'bg-blue-100 text-blue-700';
    if (count <= 20) return 'bg-indigo-100 text-indigo-700';
    return 'bg-violet-100 text-violet-700';
  }

  trackByTagId(_index: number, tag: Tag): string {
    return tag._id;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Color helpers (mirrors EditCategoriesComponent pattern)
  // ────────────────────────────────────────────────────────────────────────────

  getColorHex(): string {
    return this.normalizeHexValue(this.editForm.color);
  }

  getNormalizedColor(value: string | null | undefined): string {
    return this.normalizeHexValue(value);
  }

  setColorFromInput(value: string | object | null | undefined): void {
    const rawValue = this.extractColorValue(value);
    const normalized = this.normalizeHexValue(rawValue);
    this.editForm.color = normalized || rawValue;
  }

  pickCommonColor(color: string): void {
    this.editForm.color = this.normalizeHexValue(color);
  }

  isPresetSelected(color: string): boolean {
    return this.normalizeHexValue(this.editForm.color) === this.normalizeHexValue(color);
  }

  getTagChipBackground(color: string | undefined): string {
    const normalized = this.normalizeHexValue(color ?? '');
    return normalized || '#F8FAFC';
  }

  getReadableTextColor(backgroundColor: string): string {
    const normalized = this.normalizeHexValue(backgroundColor);
    if (!normalized) return '#0f172a';

    const hex = normalized.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.62 ? '#0f172a' : '#ffffff';
  }

  private normalizeHexValue(value: string | null | undefined): string {
    if (!value) return '';
    const raw = String(value).replace(/^#/, '').trim();
    if (/^[0-9a-fA-F]{3}$/.test(raw)) {
      return '#' + raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2];
    }
    if (/^[0-9a-fA-F]{6}$/.test(raw)) {
      return '#' + raw;
    }
    return value.startsWith('#') ? value : '#' + value;
  }

  private extractColorValue(value: string | object | null | undefined): string {
    if (typeof value === 'string') {
      return value;
    }

    if (!value || typeof value !== 'object') {
      return '';
    }

    const record = value as Record<string, unknown>;
    const hexCandidate = typeof record['hex'] === 'string'
      ? record['hex']
      : (typeof record['value'] === 'string' ? record['value'] : '');
    if (hexCandidate) {
      return hexCandidate;
    }

    const r = Number(record['r']);
    const g = Number(record['g']);
    const b = Number(record['b']);
    if ([r, g, b].every((channel) => Number.isFinite(channel) && channel >= 0 && channel <= 255)) {
      const toHex = (channel: number) => Math.round(channel).toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }

    return '';
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Edit / Add dialog
  // ────────────────────────────────────────────────────────────────────────────

  openAddDialog(): void {
    this.editingTag = null;
    this.editForm = this.emptyForm();
    this.editDialogVisible = true;
  }

  editTag(tag: Tag): void {
    this.editingTag = tag;
    this.editForm = {
      _id: tag._id,
      name: tag.name,
      description: tag.description ?? '',
      tagType: tag.tagType ?? '',
      slug: tag.slug ?? '',
      color: this.normalizeHexValue(tag.color ?? ''),
      icon: tag.icon ?? '',
      isActive: tag.isActive !== false,
      sortOrder: tag.sortOrder ?? null,
    };
    this.editDialogVisible = true;
  }

  resetEditDialog(): void {
    this.editDialogVisible = false;
    this.editingTag = null;
    this.editForm = this.emptyForm();
    this.isSaving = false;
  }

  saveTagEdits(): void {
    const name = this.editForm.name.trim();
    if (!name) {
      this.snackbarService.show('Tag name is required.', 'error');
      return;
    }
    if (this.isSaving) return;
    this.isSaving = true;

    const slug = this.editForm.slug.trim() || this.slugify(name);
    const normalizedColor = this.normalizeHexValue(this.extractColorValue(this.editForm.color));
    const payload: TagMutationPayload = {
      name,
      description: this.editForm.description.trim() || undefined,
      tagType: this.editForm.tagType.trim(),
      slug,
      color: normalizedColor || undefined,
      icon: this.editForm.icon.trim() || undefined,
      isActive: this.editForm.isActive,
      sortOrder: this.editForm.sortOrder ?? undefined,
    };

    const op$ = this.editingTag
      ? this.dataService.updateTag(this.editingTag._id, payload)
      : this.dataService.createTag(payload);

    op$.pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: (resp) => {
        if (resp.status === 'error' || !resp.data) {
          this.snackbarService.show(resp.error?.message ?? 'Save failed.', 'error');
          return;
        }
        const saved = {
          ...resp.data,
          color: this.normalizeHexValue(resp.data.color ?? normalizedColor),
        };
        if (this.editingTag) {
          const idx = this.tags.findIndex((t) => t._id === saved._id);
          if (idx !== -1) {
            this.tags = [
              ...this.tags.slice(0, idx),
              saved,
              ...this.tags.slice(idx + 1),
            ].sort((a, b) => a.name.localeCompare(b.name));
          }
          this.snackbarService.show(`Tag "${saved.name}" updated.`, 'success');
        } else {
          this.tags = [...this.tags, saved].sort((a, b) => a.name.localeCompare(b.name));
          this.snackbarService.show(`Tag "${saved.name}" created.`, 'success');
        }
        this.resetEditDialog();

        // Refresh app cache
        this.dataService.refreshLocalData().catch((err) => console.error('EditTags: refreshLocalData error', err));
      },
      error: (err) => {
        console.error('EditTags: save error', err);
        this.snackbarService.show('Unexpected error saving tag.', 'error');
      },
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Delete
  // ────────────────────────────────────────────────────────────────────────────

  deleteTag(tag: Tag): void {
    if (!this.canDelete(tag._id)) return;

    this.confirmationService.confirm({
      header: 'Delete Tag',
      message: `Delete tag "<strong>${tag.name}</strong>"? This cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.dataService.deleteTag(tag._id).subscribe({
          next: (resp) => {
            if (resp.status === 'error') {
              this.snackbarService.show(resp.error?.message ?? 'Delete failed.', 'error');
              return;
            }
            this.tags = this.tags.filter((t) => t._id !== tag._id);
            this.usageCounts.delete(tag._id);
            this.snackbarService.show(`Tag "${tag.name}" deleted.`, 'success');

            this.dataService.refreshLocalData().catch((err) => console.error('EditTags: refreshLocalData error after delete', err));
          },
          error: (err) => {
            console.error('EditTags: delete error', err);
            this.snackbarService.show('Unexpected error deleting tag.', 'error');
          },
        });
      },
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Private utilities
  // ────────────────────────────────────────────────────────────────────────────

  private emptyForm(): TagEditForm {
    return {
      _id: '',
      name: '',
      description: '',
      tagType: '',
      slug: '',
      color: '',
      icon: '',
      isActive: true,
      sortOrder: null,
    };
  }

  private slugify(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
